// Rewards API Service
// Based on Rewards API Documentation
import AsyncStorage from '@react-native-async-storage/async-storage';
import { handleApiResponse } from './userDataService';
import { makeAuthenticatedApiCall, getSessionId, getRememberToken } from './authApi';

// Debug function to check session state
export const debugSessionState = async () => {
  try {
    const sessionId = await getSessionId();
    const rememberToken = await getRememberToken();
    const allKeys = await AsyncStorage.getAllKeys();
    const sessionKeys = allKeys.filter(key => key.includes('session') || key.includes('token') || key.includes('user'));
    
    console.log('=== SESSION DEBUG INFO ===');
    console.log('Session ID:', sessionId);
    console.log('Remember Token:', rememberToken ? 'Present' : 'Missing');
    console.log('Relevant AsyncStorage keys:', sessionKeys);
    
    if (sessionKeys.length > 0) {
      const values = await AsyncStorage.multiGet(sessionKeys);
      console.log('AsyncStorage values:', values);
    }
    
    return {
      sessionId,
      rememberToken,
      hasSession: !!sessionId,
      hasRememberToken: !!rememberToken,
      storageKeys: sessionKeys
    };
  } catch (error) {
    console.error('Error debugging session state:', error);
    return null;
  }
};

import { AUTH_API_URL, ZCLUB_API_URL } from '../config/api';

const API_BASE_URL = AUTH_API_URL;

const tukarPoinUrl = (path = '', query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return `${ZCLUB_API_URL}/tukar-poin${path}${qs ? `?${qs}` : ''}`;
};

export const getTukarPoinCatalog = async (filters = {}) => {
  const result = await makeAuthenticatedApiCall(
    tukarPoinUrl('/catalog', {
      page: filters.page || 1,
      limit: filters.limit || 200,
      search: filters.search || '',
      kategori: filters.kategori || '',
    }),
    { method: 'GET' },
  );
  return result;
};

export const logTukarPoinMenuOpen = async (platform = 'club') => {
  try {
    return await makeAuthenticatedApiCall(tukarPoinUrl('/menu-open'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform }),
    });
  } catch (error) {
    console.warn('logTukarPoinMenuOpen failed:', error?.message || error);
    return null;
  }
};

export const getTukarPoinDetail = async (rewardId) => {
  const result = await makeAuthenticatedApiCall(
    tukarPoinUrl(`/catalog/${rewardId}`),
    { method: 'GET' },
  );
  return result;
};

export const getTukarPoinRiwayat = async (page = 1, limit = 20) => {
  const result = await makeAuthenticatedApiCall(
    tukarPoinUrl('/riwayat', { page, limit }),
    { method: 'GET' },
  );
  return result;
};

// Get Rewards with pagination and filters
export const getRewards = async (filters = {}) => {
  try {
    console.log('getRewards called with filters:', filters);
    
    // Debug session state first
    await debugSessionState();
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add filter parameters
    if (filters.kategori) params.append('kategori', filters.kategori);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/getRewards${queryString ? `?${queryString}` : ''}`;
    
    console.log('Making API call to:', url);
    
    // Use makeAuthenticatedApiCall for automatic session management
    const result = await makeAuthenticatedApiCall(url, {
      method: 'GET',
    });
    
    console.log('getRewards API call successful');
    return result;
  } catch (error) {
    console.error('getRewards API call failed:', error);
    throw new Error(error.message || 'Failed to fetch rewards');
  }
};

// Get rewards with default filters for catalog
export const getRewardsCatalog = async (page = 1, limit = 10, search = '') => {
  return await getRewards({
    page,
    limit,
    status: 'aktif',
    search,
  });
};

export const getUserRewards = async (page = 1, limit = 20) => {
  return getTukarPoinRiwayat(page, limit);
};

export const quoteTukarPoinOngkir = async (rewardId, extra = {}) => {
  try {
    const result = await makeAuthenticatedApiCall(`${ZCLUB_API_URL}/tukar-poin/ongkir`, {
      method: 'POST',
      body: JSON.stringify({
        id_reward: rewardId,
        id_alamat: extra.id_alamat || extra.idAlamat || 0,
        id_produk: extra.id_produk || extra.produk_id || extra.sku_id || 0,
      }),
    });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to quote shipping');
  }
};

export const getPaymentInfo = async (rewardId) => {
  try {
    const result = await makeAuthenticatedApiCall(`${ZCLUB_API_URL}/tukar-poin/shipping/payment/${rewardId}`, {
      method: 'POST',
    });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to get payment info');
  }
};

export const cancelRedemption = async (redemptionId, reason = '') => {
  try {
    const result = await makeAuthenticatedApiCall(`${ZCLUB_API_URL}/tukar-poin/cancel/${redemptionId}`, {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || '',
      }),
    });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to cancel reward redemption');
  }
};

// Redeem a reward (placeholder for future implementation)
export const redeemReward = async (rewardId, extra = {}) => {
  try {
    const result = await makeAuthenticatedApiCall(`${ZCLUB_API_URL}/tukar-poin/claim`, {
      method: 'POST',
      body: JSON.stringify({
        id_reward: rewardId,
        id_produk: extra.id_produk || extra.produk_id || extra.sku_id || 0,
        id_alamat: extra.id_alamat || extra.idAlamat || 0,
        metode_ongkir: extra.metode_ongkir || extra.metodeOngkir || extra.metode || '',
        kode_kurir: extra.kode_kurir || extra.courier_code || extra.kodeKurir || '',
        kode_servis: extra.kode_servis || extra.courier_service || extra.kodeServis || '',
        ongkir: extra.ongkir || extra.ongkir_nominal || extra.harga || 0,
        id_log_kurir: extra.id_log_kurir || extra.idLogKurir || 0,
        tipe_pengiriman: extra.tipe_pengiriman || extra.tipePengiriman || '',
        asuransi_pengiriman: extra.asuransi_pengiriman || extra.asuransiPengiriman || 0,
        asuransi_barang: extra.asuransi_barang || extra.asuransiBarang || 0,
        poin_digunakan: extra.poin_digunakan || extra.poinDigunakan || 0,
        metode_pembayaran: extra.metode_pembayaran || extra.metodePembayaran || '',
        catatan_customer: extra.catatan_customer || extra.catatanCustomer || null,
      }),
    });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to redeem reward');
  }
};

// Helper function to format reward data for display
export const formatRewardForDisplay = (reward) => {
  return {
    id: reward.id,
    title: reward.nama_reward,
    description: reward.deskripsi,
    pointsCost: reward.harga_poin,
    estampCost: reward.harga_estamp,
    category: reward.kategori,
    image: reward.gambar_reward ? { uri: reward.gambar_reward } : require('../../assets/rewards/reward-image-1.png'),
    availability: `Tersisa ${reward.stok_tersedia}+ kupon!`,
    stockStatus: reward.stok_tersedia > 10 ? 'available' : 'limited',
    status: reward.status,
    termsConditions: reward.syarat_ketentuan,
    slaHariKerja: reward.sla_hari_kerja || null,
    slaCatatan: reward.sla_catatan || '',
    perluPengiriman: !!reward.perlu_pengiriman,


  };
};

// Helper function to format category data
export const formatCategoryForDisplay = (category) => {
  return {
    id: category.id,
    name: category.nama_kategori,
    description: category.deskripsi,
    icon: category.ikon_kategori,
    order: category.urutan_tampil,
  };
};

export default {
  getRewards,
  getRewardsCatalog,
  getTukarPoinCatalog,
  getTukarPoinDetail,
  getTukarPoinRiwayat,
  getUserRewards,
  redeemReward,
  quoteTukarPoinOngkir,
  formatRewardForDisplay,
  formatCategoryForDisplay,
};